import React from 'react';
import { useTranslation } from 'react-i18next';
import { Languages } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { changeLanguage } from '../i18n';

interface LanguageSwitcherProps {
  variant?: 'icon' | 'inline';
  className?: string;
}

const FLAG = {
  pt: '🇧🇷',
  en: '🇺🇸',
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'icon', className = '' }) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.startsWith('en') ? 'en' : 'pt';

  const handleChange = (value: string) => {
    changeLanguage(value as 'pt' | 'en');
  };

  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-1 text-sm ${className}`}>
        <button
          type="button"
          onClick={() => handleChange('pt')}
          className={`px-2 py-1 rounded ${currentLang === 'pt' ? 'font-semibold underline' : 'opacity-60 hover:opacity-100'}`}
        >
          {FLAG.pt} PT
        </button>
        <span className="opacity-40">|</span>
        <button
          type="button"
          onClick={() => handleChange('en')}
          className={`px-2 py-1 rounded ${currentLang === 'en' ? 'font-semibold underline' : 'opacity-60 hover:opacity-100'}`}
        >
          {FLAG.en} EN
        </button>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`hover:bg-gray-200 dark:hover:bg-slate-700 rounded-full dark:text-gray-500 ${className}`}
          title={t('language.select')}
        >
          <Languages className="h-4 w-4" />
          <span className="sr-only">{t('language.select')}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="dark:bg-slate-800">
        <DropdownMenuRadioGroup value={currentLang} onValueChange={handleChange}>
          <DropdownMenuRadioItem value="pt" className="cursor-pointer dark:text-gray-500">
            <span className="mr-2">{FLAG.pt}</span>
            <span>{t('language.portuguese')}</span>
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="en" className="cursor-pointer dark:text-gray-500">
            <span className="mr-2">{FLAG.en}</span>
            <span>{t('language.english')}</span>
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;
